import { X } from 'lucide-react'
import type React from 'react'
import { useCallback, useEffect, useState } from 'react'
import styled from 'styled-components'
import { supabase } from '../../lib/supabase'
import { theme } from '../../theme/theme'
import type { Profile } from '../../types/database.tables'
import { deleteAvatar, getAvatarUrl, updateProfileAvatar, uploadAvatar } from '../../utils/avatarUtils'
import { AvatarUpload } from '../common/AvatarUpload'

const ModalOverlay = styled.div<{ $isOpen: boolean }>`
  display: ${({ $isOpen }) => ($isOpen ? 'flex' : 'none')};
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  align-items: center;
  justify-content: center;
  z-index: 2000;
  padding: ${theme.spacing.md};
`

const ModalContent = styled.div`
  background: ${({ theme }) => theme.colors.cardBackground};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${theme.borderRadius.lg};
  box-shadow: ${({ theme }) => theme.shadows.lg};
  width: 100%;
  max-width: 500px;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${theme.spacing.lg};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
`

const ModalTitle = styled.h2`
  font-size: ${theme.typography.lg};
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text};
  margin: 0;
`

const CloseButton = styled.button`
  background: none;
  border: none;
  color: ${({ theme }) => theme.colors.textSecondary};
  cursor: pointer;
  padding: ${theme.spacing.xs};
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: ${theme.borderRadius.sm};
  transition: ${theme.transitions.default};

  &:hover {
    background: ${({ theme }) => theme.colors.surface};
    color: ${({ theme }) => theme.colors.text};
  }
`

const ModalBody = styled.div`
  padding: ${theme.spacing.xl};
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.xl};
`

const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.md};
`

const SectionTitle = styled.h3`
  font-size: ${theme.typography.md};
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text};
  margin: 0;
`

const FormField = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.xs};
`

const Label = styled.label`
  font-size: ${theme.typography.sm};
  font-weight: 500;
  color: ${({ theme }) => theme.colors.text};
`

const Input = styled.input`
  padding: ${theme.spacing.sm} ${theme.spacing.md};
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${theme.borderRadius.sm};
  color: ${({ theme }) => theme.colors.text};
  font-size: ${theme.typography.md};
  transition: ${theme.transitions.default};

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`

const Select = styled.select`
  padding: ${theme.spacing.sm} ${theme.spacing.md};
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${theme.borderRadius.sm};
  color: ${({ theme }) => theme.colors.text};
  font-size: ${theme.typography.md};
  transition: ${theme.transitions.default};

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`

const ButtonGroup = styled.div`
  display: flex;
  gap: ${theme.spacing.sm};
  justify-content: flex-end;
  padding-top: ${theme.spacing.md};
  border-top: 1px solid ${({ theme }) => theme.colors.border};
`

const Button = styled.button<{ $variant?: 'primary' | 'secondary' }>`
  padding: ${theme.spacing.sm} ${theme.spacing.lg};
  border-radius: ${theme.borderRadius.sm};
  border: none;
  font-size: ${theme.typography.md};
  font-weight: 500;
  cursor: pointer;
  transition: ${theme.transitions.default};

  ${({ $variant = 'secondary', theme }) =>
    $variant === 'primary'
      ? `
    background: ${theme.colors.primary};
    color: ${theme.colors.textInverted};
    &:hover:not(:disabled) {
      opacity: 0.9;
    }
  `
      : `
    background: ${theme.colors.surface};
    color: ${theme.colors.text};
    border: 1px solid ${theme.colors.border};
    &:hover:not(:disabled) {
      background: ${theme.colors.surfaceHover};
      border-color: ${theme.colors.borderHover};
    }
  `}

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`

const ErrorMessage = styled.div`
  color: ${theme.colors.error};
  font-size: ${theme.typography.sm};
  padding: ${theme.spacing.sm};
  background: ${theme.colors.error}20;
  border-radius: ${theme.borderRadius.sm};
`

const SuccessMessage = styled.div`
  color: ${theme.colors.success};
  font-size: ${theme.typography.sm};
  padding: ${theme.spacing.sm};
  background: ${theme.colors.success}20;
  border-radius: ${theme.borderRadius.sm};
`

interface AthleteProfileModalProps {
  isOpen: boolean
  onClose: () => void
  athleteId: string
  athleteName: string
  onProfileUpdated?: () => void
}

export const AthleteProfileModal: React.FC<AthleteProfileModalProps> = ({
  isOpen,
  onClose,
  athleteId,
  athleteName,
  onProfileUpdated,
}) => {
  const [profile, setProfile] = useState<Profile | null>(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)

  // Form state
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [birthday, setBirthday] = useState('')
  const [gender, setGender] = useState('')

  const loadProfile = useCallback(async () => {
    setLoading(true)
    setError(null)

    try {
      const { data, error: loadError } = await supabase.from('profiles').select('*').eq('id', athleteId).single()
      if (loadError) throw loadError

      setProfile(data)
      setFirstName(data.first_name || '')
      setLastName(data.last_name || '')
      setBirthday(data.birthday || '')
      setGender(data.gender || '')
    } catch (err) {
      console.error('Error loading profile:', err)
      setError(err instanceof Error ? err.message : 'Failed to load profile')
    } finally {
      setLoading(false)
    }
  }, [athleteId])

  // Load athlete profile
  useEffect(() => {
    if (isOpen && athleteId) {
      loadProfile()
    }
  }, [isOpen, athleteId, loadProfile])

  const handleSave = async () => {
    setSaving(true)
    setError(null)
    setSuccess(null)

    try {
      const { error: updateError } = await supabase
        .from('profiles')
        .update({
          first_name: firstName || null,
          last_name: lastName || null,
          birthday: birthday || null,
          gender: gender || null,
        })
        .eq('id', athleteId)

      if (updateError) throw updateError

      setSuccess('Profile updated successfully!')

      // Reload profile to get updated data
      await loadProfile()

      // Notify parent to refresh data
      if (onProfileUpdated) {
        onProfileUpdated()
      }

      // Close modal after a short delay
      setTimeout(() => {
        onClose()
      }, 1500)
    } catch (err) {
      console.error('Error updating profile:', err)
      setError(err instanceof Error ? err.message : 'Failed to update profile')
    } finally {
      setSaving(false)
    }
  }

  const handleUpload = async (file: File) => {
    try {
      // Delete old avatar if exists
      if (profile?.avatar_url) {
        await deleteAvatar(profile.avatar_url)
      }

      // Upload new avatar
      const uploadResult = await uploadAvatar(athleteId, file)

      if (!uploadResult.success || !uploadResult.path) {
        return uploadResult
      }

      // Update profile with new avatar path
      const updateResult = await updateProfileAvatar(athleteId, uploadResult.path)

      if (!updateResult.success) {
        return { success: false, error: updateResult.error }
      }

      // Refresh profile
      await loadProfile()

      // Notify parent to refresh data
      if (onProfileUpdated) {
        onProfileUpdated()
      }

      return uploadResult
    } catch (error) {
      console.error('Avatar upload error:', error)
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to upload avatar',
      }
    }
  }

  const handleRemoveAvatar = async () => {
    if (!profile?.avatar_url) return

    try {
      // Delete from storage
      await deleteAvatar(profile.avatar_url)

      // Update profile to remove avatar_url
      await updateProfileAvatar(athleteId, '')

      // Refresh profile
      await loadProfile()

      // Notify parent to refresh data
      if (onProfileUpdated) {
        onProfileUpdated()
      }
    } catch (error) {
      console.error('Avatar remove error:', error)
      throw error
    }
  }

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose()
    }
  }

  // Close on escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose()
      }
    }

    document.addEventListener('keydown', handleEscape)
    return () => document.removeEventListener('keydown', handleEscape)
  }, [isOpen, onClose])

  const currentAvatarUrl = profile?.avatar_url ? getAvatarUrl(profile.avatar_url) : null
  const fullName = `${firstName} ${lastName}`.trim() || athleteName

  if (!isOpen) return null

  return (
    <ModalOverlay $isOpen={isOpen} onClick={handleOverlayClick}>
      <ModalContent onClick={(e) => e.stopPropagation()}>
        <ModalHeader>
          <ModalTitle>Edit Athlete Profile</ModalTitle>
          <CloseButton onClick={onClose}>
            <X size={20} />
          </CloseButton>
        </ModalHeader>

        <ModalBody>
          {loading ? (
            <div>Loading profile...</div>
          ) : (
            <>
              <Section>
                <SectionTitle>Profile Picture</SectionTitle>
                <AvatarUpload
                  currentAvatarUrl={currentAvatarUrl}
                  userName={fullName}
                  size={120}
                  onUpload={handleUpload}
                  onRemove={handleRemoveAvatar}
                  disabled={saving}
                />
              </Section>

              <Section>
                <SectionTitle>Personal Information</SectionTitle>

                <FormField>
                  <Label htmlFor="firstName">First Name</Label>
                  <Input
                    id="firstName"
                    type="text"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    disabled={saving}
                    placeholder="Enter first name"
                  />
                </FormField>

                <FormField>
                  <Label htmlFor="lastName">Last Name</Label>
                  <Input
                    id="lastName"
                    type="text"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    disabled={saving}
                    placeholder="Enter last name"
                  />
                </FormField>

                <FormField>
                  <Label htmlFor="birthday">Birthday</Label>
                  <Input
                    id="birthday"
                    type="date"
                    value={birthday}
                    onChange={(e) => setBirthday(e.target.value)}
                    disabled={saving}
                  />
                </FormField>

                <FormField>
                  <Label htmlFor="gender">Gender</Label>
                  <Select id="gender" value={gender} onChange={(e) => setGender(e.target.value)} disabled={saving}>
                    <option value="">Select gender</option>
                    <option value="male">Male</option>
                    <option value="female">Female</option>
                    <option value="other">Other</option>
                  </Select>
                </FormField>
              </Section>

              {error && <ErrorMessage>{error}</ErrorMessage>}
              {success && <SuccessMessage>{success}</SuccessMessage>}

              <ButtonGroup>
                <Button onClick={onClose} disabled={saving}>
                  Cancel
                </Button>
                <Button $variant="primary" onClick={handleSave} disabled={saving}>
                  {saving ? 'Saving...' : 'Save Changes'}
                </Button>
              </ButtonGroup>
            </>
          )}
        </ModalBody>
      </ModalContent>
    </ModalOverlay>
  )
}
